import { PropsWithChildren, useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';

import { useGetUser } from '@/shared/modules/auth/hooks/useGetUser';
import {
  InternalContext,
  Status,
} from '@/shared/contexts/launch-darkly/InternalContext';

const TIMEOUT = 5;

const getContext = ({
  id,
  orgId,
  resourceKey,
  region,
}: {
  id: string;
  orgId: string;
  resourceKey: string;
  region: string;
}) => {
  const hasResource = resourceKey && region;

  if (hasResource) {
    return {
      kind: 'multi',
      user: {
        key: id,
        organization: orgId,
      },
      resource: {
        key: resourceKey,
        region,
      },
    };
  }

  return {
    kind: 'user',
    key: id,
    organization: orgId,
  };
};

export const InternalProvider = ({ children }: PropsWithChildren) => {
  const ldClient = useLDClient();
  const params = useParams();
  const [status, setStatus] = useState<Status>('uninitialized');
  const { data } = useGetUser();
  const idAsNumber = data?.id;
  const id = idAsNumber?.toString() ?? '';
  const orgId = data?.orgId ?? '';
  const resourceKey = params.deploymentId ?? params.projectId ?? '';
  const region = params.region ?? '';

  useEffect(() => {
    const initialize = async () => {
      if (!ldClient) return;

      const context = getContext({ id, orgId, resourceKey, region });

      try {
        setStatus((prev) => (prev === 'completed' ? prev : 'initializing'));
        await ldClient.waitForInitialization(TIMEOUT);
        await ldClient.identify(context);
        setStatus('completed');
      } catch (_error) {
        setStatus('failed');
      }
    };

    void initialize();
  }, [ldClient, id, orgId, resourceKey, region]);

  return (
    <InternalContext.Provider value={{ status }}>
      {children}
    </InternalContext.Provider>
  );
};
